// Imports remain unchanged
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../../../components/apiconfig/api-config';
import { FaPlus, FaEdit, FaEye } from 'react-icons/fa';
import NoRecordFound from '../../../../components/common/no-record-found';
import { formatDateDDMMYYEmpty } from '../../../../components/common/dateUtils';
import ViewModal from '../../../../components/common/ViewModal';

const ROWS_PER_PAGE = 35;

const SaleBrokerList = () => {
  const navigate = useNavigate();
  const [brokerRecords, setBrokerRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedBroker, setSelectedBroker] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [brokerDetails, setBrokerDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  const fetchRecords = async (page, searchValue) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page,
        limit: ROWS_PER_PAGE,
        search: searchValue || '',
      });
      const res = await fetch(`${API_BASE_URL}/api/masters/sale-broker-get?${params.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const result = await res.json();
      setBrokerRecords(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      setError('Failed to load data');
      console.error('Fetch Broker error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords(page, debouncedSearch);
    // eslint-disable-next-line
  }, [page, debouncedSearch]);

  const totalPages = Math.ceil(total / ROWS_PER_PAGE);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

  const handleViewDetails = async (broker) => {
    setSelectedBroker(broker);
    setIsViewModalOpen(true);
    setLoadingDetails(true);
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/masters/sale-broker-get/${broker.broker_id}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const result = await res.json();
        setBrokerDetails(result.data);
      } else {
        setBrokerDetails(broker); // Fallback to list data
      }
    } catch (err) {
      console.error('Error fetching broker details:', err);
      setBrokerDetails(broker); // Fallback to list data
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedBroker(null);
    setBrokerDetails(null);
  };

  return (
    <div className="max-w-8xl mx-auto p-2 bg-white rounded border border-gray-100">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
        <h2 className="text-xl font-semibold">Sale Broker Records</h2>
        <div className="flex gap-1 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={handleSearchChange}
            className="border border-gray-200 rounded px-2 py-1 focus:outline-none w-full sm:w-48 text-sm"
          />
          <button
            className="flex items-center gap-1 bg-[#10a5e9] text-white px-3 py-1 text-xs rounded hover:bg-[#0e8dc6] border border-[#10a5e9] font-medium"
            onClick={() => navigate('/broker/new')}
          >
            <FaPlus />
            Add New
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-blue-600 py-6 text-sm">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-600 py-6 text-sm">{error}</div>
      ) : brokerRecords.length === 0 ? (
        <NoRecordFound message="No Broker records found." />
      ) : (
        <div className="overflow-x-auto rounded border border-gray-50">
          <table className="min-w-full text-sm whitespace-nowrap">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Name</th>
                <th className="px-3 py-2 text-left font-medium">Phone</th>
                <th className="px-3 py-2 text-left font-medium">GST</th>
                <th className="px-3 py-2 text-left font-medium">PAN</th>
                <th className="px-3 py-2 text-left font-medium">Bank</th>
                <th className="px-3 py-2 text-left font-medium">A/C No</th>
                <th className="px-3 py-2 text-left font-medium">IFSC</th>
                <th className="px-3 py-2 text-left font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {brokerRecords.map((broker, idx) => (
                <tr key={broker.broker_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-3 py-1 font-semibold">{broker.name}</td>
                  <td className="px-3 py-1">{broker.contact_phone}</td>
                  <td className="px-3 py-1">{broker.gst_no}</td>
                  <td className="px-3 py-1">{broker.pan_no}</td>
                  <td className="px-3 py-1">{broker.bank_name}</td>
                  <td className="px-3 py-1">{broker.account_no}</td>
                  <td className="px-3 py-1">{broker.ifsc_code}</td>
                  <td className="px-3 py-1">
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleViewDetails(broker)}
                        className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 border border-green-600 flex items-center gap-1"
                        title="View Broker Details"
                      >
                        <FaEye />
                        View
                      </button>
                      <button
                        onClick={() => navigate(`/broker/edit/${broker.broker_id}`)}
                        className="text-xs px-2 py-1 bg-[#10a5e9] text-white rounded hover:bg-[#0e8dc6] border border-[#10a5e9] flex items-center gap-1"
                        title="Edit Sale Broker"
                      >
                        <FaEdit />
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination Controls */}
      {total > ROWS_PER_PAGE && (
        <div className="flex justify-end items-center gap-2 mt-3 text-xs">
          <button
            onClick={handlePrevPage}
            disabled={page === 1}
            className={`px-2 py-1 rounded border transition-colors duration-100 ${page === 1 ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'}`}
          >
            Prev
          </button>
          <span className="text-gray-600">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={handleNextPage}
            disabled={page === totalPages}
            className={`px-2 py-1 rounded border transition-colors duration-100 ${page === totalPages ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'}`}
          >
            Next
          </button>
        </div>
      )}

      {/* View Modal */}
      <ViewModal
        isOpen={isViewModalOpen}
        onClose={closeViewModal}
        title={`Broker Details - ${brokerDetails?.name || selectedBroker?.name || ''}`}
        size="xl"
      >
        {loadingDetails ? (
          <div className="text-center py-8 text-blue-600">Loading details...</div>
        ) : brokerDetails ? (
          <div className="space-y-6">
            {/* Basic Broker Information */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-600">Broker Name</label>
                  <p className="text-sm font-semibold text-gray-900">{brokerDetails.name || '-'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">Contact Phone</label>
                  <p className="text-sm text-gray-900">{brokerDetails.contact_phone || '-'}</p>
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-medium text-gray-600">Address</label>
                  <p className="text-sm text-gray-900">{brokerDetails.address || '-'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">GST No</label>
                  <p className="text-sm text-gray-900">{brokerDetails.gst_no || '-'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">PAN No</label>
                  <p className="text-sm text-gray-900">{brokerDetails.pan_no || '-'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">Aadhar No</label>
                  <p className="text-sm text-gray-900">{brokerDetails.aadhar_no || '-'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">Email</label>
                  <p className="text-sm text-gray-900">{brokerDetails.email || '-'}</p>
                </div>
              </div>
            </div>

            {/* Bank Information */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Bank Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-600">Bank Name</label>
                  <p className="text-sm text-gray-900">{brokerDetails.bank_name || '-'}</p>
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-medium text-gray-600">Bank Address</label>
                  <p className="text-sm text-gray-900">{brokerDetails.bank_address || '-'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">Account No</label>
                  <p className="text-sm text-gray-900">{brokerDetails.account_no || '-'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">IFSC Code</label>
                  <p className="text-sm text-gray-900">{brokerDetails.ifsc_code || '-'}</p>
                </div>
              </div>
            </div>

            {/* Commodity & Commission Details */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Commodity & Commission Details</h3>
              {brokerDetails.commodity_entries && brokerDetails.commodity_entries.length > 0 ? (
                <div className="space-y-4">
                  {brokerDetails.commodity_entries.map((entry, idx) => (
                    <div key={idx} className="bg-white p-4 rounded border border-gray-300">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-semibold text-gray-700">Entry #{idx + 1}</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs font-medium text-gray-600">Commodity</label>
                          <p className="text-sm text-gray-900">
                            {entry.commodity_name || '-'}
                            {entry.commodity_origin && (
                              <span className="text-gray-500 ml-1">({entry.commodity_origin})</span>
                            )}
                          </p>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-600">Commission Type</label>
                          <p className="text-sm text-gray-900">{entry.commission_type || '-'}</p>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-600">Commission Value</label>
                          <p className="text-sm text-gray-900">{entry.commission_value || '-'}</p>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-600">State</label>
                          <p className="text-sm text-gray-900">{entry.state || '-'}</p>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-600">State Code</label>
                          <p className="text-sm text-gray-900">{entry.state_code || '-'}</p>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-600">Effective Date</label>
                          <p className="text-sm text-gray-900">{formatDateDDMMYYEmpty(entry.effective_date) || '-'}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No commodity entries found.</p>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-red-600">Failed to load broker details</div>
        )}
      </ViewModal>
    </div>
  );
};

export default SaleBrokerList;
